
import React, { useState } from 'react';
import OnboardingLayout from '../../OnboardingLayout';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { FileUp, Link as LinkIcon, FileText, AlertCircle } from 'lucide-react';

interface ContentImportProps {
  formData: any;
  updateFormData: (data: any) => void;
  currentStep: number;
  totalSteps: number;
  onNext: () => void;
  onPrevious: () => void;
}

const ContentImport: React.FC<ContentImportProps> = ({
  formData,
  updateFormData,
  currentStep,
  totalSteps,
  onNext,
  onPrevious
}) => {
  const [selectedMethod, setSelectedMethod] = useState('url');
  const [urls, setUrls] = useState<string[]>([]);
  const [currentUrl, setCurrentUrl] = useState('');
  
  const handleAddUrl = () => {
    if (currentUrl.trim() && !urls.includes(currentUrl.trim())) {
      const newUrls = [...urls, currentUrl.trim()];
      setUrls(newUrls);
      setCurrentUrl('');
      updateFormData({ importedContent: newUrls });
    }
  };
  
  const handleRemoveUrl = (urlToRemove: string) => {
    const newUrls = urls.filter(url => url !== urlToRemove);
    setUrls(newUrls);
    updateFormData({ importedContent: newUrls });
  };

  return (
    <OnboardingLayout
      title="Import Your Existing Content"
      subtitle="Bring your previously published work to DirectPress"
      currentStep={currentStep}
      totalSteps={totalSteps}
      onNext={onNext}
      onPrevious={onPrevious}
    >
      <div className="max-w-3xl mx-auto">
        <Tabs defaultValue="url" onValueChange={setSelectedMethod} className="w-full mb-6">
          <TabsList className="grid grid-cols-3 mb-6">
            <TabsTrigger value="url">URL Import</TabsTrigger>
            <TabsTrigger value="file">File Upload</TabsTrigger>
            <TabsTrigger value="manual">Manual Entry</TabsTrigger>
          </TabsList>
          
          <TabsContent value="url" className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Import from URLs</h3>
                <p className="text-muted-foreground mb-4">
                  Add links to your published articles from other platforms. We'll extract the content
                  and prepare them for publishing on DirectPress.
                </p>
                
                <div className="flex items-end gap-2 mb-4">
                  <div className="flex-1">
                    <Label htmlFor="url-input">Article URL</Label>
                    <Input 
                      id="url-input"
                      placeholder="https://example.com/your-article"
                      value={currentUrl}
                      onChange={(e) => setCurrentUrl(e.target.value)}
                      className="mt-1"
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddUrl();
                        }
                      }}
                    />
                  </div>
                  <Button 
                    onClick={handleAddUrl}
                    disabled={!currentUrl.trim()}
                  >
                    Add
                  </Button>
                </div>
                
                {urls.length > 0 ? (
                  <div className="border rounded-md divide-y">
                    {urls.map((url, index) => (
                      <div key={index} className="flex items-center justify-between p-3">
                        <div className="text-sm truncate flex-1">{url}</div>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => handleRemoveUrl(url)}
                        >
                          Remove
                        </Button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 border rounded-md text-muted-foreground">
                    <LinkIcon className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>No URLs added yet</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="file" className="space-y-6">
            <Card>
              <CardContent className="pt-6">
                <h3 className="text-lg font-medium mb-4">Upload Document Files</h3>
                <p className="text-muted-foreground mb-4">
                  Upload Word documents, PDFs, or text files containing your articles.
                </p>
                
                <div className="border border-dashed rounded-lg p-8 text-center">
                  <FileUp className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                  <h4 className="text-base font-medium mb-2">Drag & drop files here</h4>
                  <p className="text-sm text-muted-foreground mb-4">
                    Supports .docx, .pdf, .txt, .rtf, .md files
                  </p>
                  <Button>
                    Select Files
                  </Button>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
          
          <TabsContent value="manual" className="space-y-6">
            <Card>
              <CardContent className="pt-6 text-center">
                <FileText className="h-10 w-10 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-4">Manual Content Entry</h3>
                <p className="text-muted-foreground mb-4">
                  You'll be able to create new articles manually after completing the onboarding process.
                </p>
                <Button variant="outline" onClick={onNext}>
                  Skip Import & Continue
                </Button>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
        
        <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <AlertCircle className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
          <div>
            <h4 className="text-sm font-medium mb-1">Content ownership</h4>
            <p className="text-xs text-muted-foreground">
              Make sure you have the rights to republish any content you import. If the content is under 
              an exclusive agreement with another platform, check their terms before importing.
            </p>
          </div>
        </div>
      </div>
    </OnboardingLayout>
  );
};

export default ContentImport;
